import { useEffect, useState } from 'react';
import { Text, rem, Skeleton, Stack, Group, ThemeIcon, Box } from '@mantine/core';
import { motion } from 'framer-motion';
import { IconRobot, IconFlame } from '@tabler/icons-react';
import { COLORS, GAME_DEFAULTS } from '../../config/constants';
import { fetchStatsRequest } from '../../services/api';

const statsConfig = [
    { id: 'active_bots', label: 'Live Bots', icon: IconFlame, color: COLORS.ORANGE, suffix: '' },
    { id: 'today_bots', label: 'Bots Today', icon: IconRobot, color: COLORS.BLUE, suffix: '' },
];

/**
 * StatsStrip component fetches and displays live global statistics from the backend.
 * It periodically refreshes the data to show active bot counts and historical stats.
 */
export default function StatsStrip() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    /**
     * Fetches current stats from the API and updates local state.
     */
    const fetchStats = async () => {
        try {
            const data = await fetchStatsRequest();
            setStats(data);
        } catch (error) {
            console.error("Failed to fetch stats", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        const interval = setInterval(fetchStats, GAME_DEFAULTS.STATS_REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(num);
    };

    return (
        <Group gap={40} justify="center" align="center" style={{ width: '100%' }}>
            {statsConfig.map((stat) => (
                <Group key={stat.id} gap="xs" align="center" wrap="nowrap">
                    <Box style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <motion.div
                            animate={stat.id === 'active_bots' ? {
                                filter: [
                                    `drop-shadow(0 0 4px var(--mantine-color-${stat.color}-6))`,
                                    `drop-shadow(0 0 10px var(--mantine-color-${stat.color}-6))`,
                                    `drop-shadow(0 0 4px var(--mantine-color-${stat.color}-6))`
                                ]
                            } : {}}
                            transition={{
                                duration: 5,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <ThemeIcon
                                variant="transparent"
                                size="lg"
                                color={stat.color}
                            >
                                <stat.icon style={{ width: rem(26), height: rem(26) }} stroke={2} />
                            </ThemeIcon>
                        </motion.div>
                    </Box>

                    <Stack gap={0} justify="center">
                        {loading ? (
                            <Skeleton height={20} width={40} radius="sm" opacity={0.3} mb={2} />
                        ) : (
                            <Text fz={20} fw={700} c="white" style={{ lineHeight: 1, fontFamily: '"Orbitron", sans-serif' }}>
                                {stats ? formatNumber(stats[stat.id]) : '0'}{stat.suffix}
                            </Text>
                        )}
                        <Text size="10px" fw={600} c="dimmed" tt="uppercase" ls={1}>
                            {stat.label}
                        </Text>
                    </Stack>
                </Group>
            ))}
            <style>{`
                @keyframes pulse {
                    0% { opacity: 0.8; transform: scale(1); }
                    50% { opacity: 0.4; transform: scale(1.2); }
                    100% { opacity: 0.8; transform: scale(1); }
                }
            `}</style>
        </Group>
    );
}